import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Download, Monitor, Tablet, Smartphone, Search, Users, Phone, FileText, CreditCard, FileSignature, Shield, BookOpen, UserCircle, MessageSquare, GraduationCap, Calendar, Play, Microscope, MapPin, Stethoscope, Heart } from 'lucide-react';
import { triggerInstall, onInstallAvailable } from './PWAInstallPrompt';
import { useSiteVisibility } from '@/lib/siteVisibility';

const Footer = () => {
  const { isHidden } = useSiteVisibility();
  const currentYear = new Date().getFullYear();
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isOphtalmoConnected = !!user && user.role === 'ophthalmologist';

  useEffect(() => {
    const unsub = onInstallAvailable((available) => setInstallAvailable(available));
    return unsub;
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const userType = user?.role || 'unknown';
    if (installAvailable) await triggerInstall(userType);
    setTimeout(() => setInstalling(false), 2000);
  };

  const showPWA = !isStandalone;

  return (
    <>
      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <footer className="relative text-white overflow-hidden" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="site-footer">
        <div className="absolute inset-0">
          <img src="/praticien.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1e3a5f]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">

            {/* Col 1: Brand + Install */}
            <div>
              <img src="/SFOALC-footer-logo.png" alt="SFOALC" className="h-16 mb-4" />
              <p className="text-white/90 text-sm font-semibold leading-relaxed mb-4">
                Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact
              </p>
              <div className="w-12 h-[2px] bg-[#35AFE4] mb-4" />
              <a href="mailto:contact@sfo-alc.fr" className="flex items-center gap-2 text-white/80 hover:text-[#7fc4e0] text-sm font-semibold transition-colors">
                <Mail size={14} /> contact@sfo-alc.fr
              </a>

              {showPWA && (
                <button onClick={handleInstall}
                  className="mt-5 w-full group relative"
                  data-testid="pwa-install-btn">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-full border-2 transition-all duration-500 ${installing ? 'border-[#35AFE4] bg-[#35AFE4]/20' : 'border-[#35AFE4]/30 bg-[#35AFE4]/5 hover:border-[#35AFE4]/70 hover:bg-[#35AFE4]/15'}`}>
                    <div className={`w-9 h-9 rounded-full bg-[#35AFE4] flex items-center justify-center flex-shrink-0 transition-all duration-500 ${installing ? 'scale-90' : 'group-hover:scale-110 group-hover:shadow-[0_0_16px_rgba(58,143,183,0.5)]'}`}>
                      <Download size={16} className="text-white animate-[downloadBounce_2s_ease-in-out_infinite]" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-white text-[13px] font-bold leading-tight truncate">
                        {installing ? 'Installation...' : "Installer l'app"}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Monitor size={11} className="text-white/50" />
                        <Tablet size={11} className="text-white/50" />
                        <Smartphone size={11} className="text-white/50" />
                        <span className="text-white/50 text-[11px]">iOS / Android / PC</span>
                      </div>
                    </div>
                  </div>
                </button>
              )}
            </div>

            {/* Col 2: L'association — with icons */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>L'association</h3>
              <ul className="space-y-2 mb-4">
                {!isHidden('/bureau') && <li><Link to="/bureau" className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors"><Users size={16} className="text-white/50" /> Le Bureau</Link></li>}
                {!isHidden('/contact') && <li><Link to="/contact" className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors"><Phone size={16} className="text-white/50" /> Contact</Link></li>}
                {!isHidden('/nos-formations') && <li><Link to="/nos-formations?cat=Les Rapports" className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors"><FileText size={16} className="text-white/50" /> Les rapports SFO</Link></li>}
              </ul>
              {!isHidden('/adhesion') && <div className="space-y-2 mb-4">
                <Link to="/adhesion" className="flex items-center gap-2 px-4 py-1.5 bg-[#35AFE4] rounded-full text-[12px] font-medium text-white hover:bg-[#1e9bd4] transition-all w-fit" data-testid="footer-adhesion-online">
                  <CreditCard size={12} />
                  Adhésion "EN LIGNE"
                </Link>
                <Link to="/adhesion/courrier" className="flex items-center gap-2 px-4 py-1.5 bg-[#2c7ea3] rounded-full text-[12px] font-medium text-white hover:bg-[#256f91] transition-all w-fit" data-testid="footer-adhesion-courrier">
                  <FileSignature size={12} />
                  Adhésion "Hors ligne"
                </Link>
              </div>}

              {/* Donate button with animated heart pulse */}
              <a href="https://www.helloasso.com/associations/sfoalc-societe-francaise-des-ophtalmologistes-adaptateurs-de-lentilles-de-contact" target="_blank" rel="noopener noreferrer"
                className="group flex items-center gap-3 mt-2" data-testid="footer-donate-btn">
                <div className="relative flex items-center justify-center w-9 h-9">
                  <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                  <span className="absolute inset-0.5 rounded-full bg-rose-500/10 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.3s' }} />
                  <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:scale-110 transition-transform">
                    <Heart size={16} className="text-white fill-white" />
                  </div>
                </div>
                <div>
                  <p className="text-rose-300 text-sm font-bold group-hover:text-rose-200 transition-colors">Soutenir l'association</p>
                  <p className="text-white/40 text-xs">Faire un don</p>
                </div>
              </a>
            </div>

            {/* Col 3: Espace Internes + Patients + Annuaire */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
                <span className="underline decoration-[#35AFE4] underline-offset-4">Espace Internes</span>
              </h3>
              <ul className="space-y-2 mb-6">
                <li><Link to="/auth" className="flex items-center gap-2 text-[#7fc4e0] hover:text-white text-sm font-semibold transition-colors"><UserCircle size={16} className="opacity-80" /> Mon compte</Link></li>
                <li><Link to="/messagerie" className="flex items-center gap-2 text-[#7fc4e0] hover:text-white text-sm font-semibold transition-colors"><MessageSquare size={16} className="opacity-80" /> Messagerie</Link></li>
                <li><Link to="/nos-formations?cat=Les Cours" className="flex items-center gap-2 text-[#7fc4e0] hover:text-white text-sm font-semibold transition-colors"><GraduationCap size={16} className="opacity-80" /> Les cours</Link></li>
              </ul>

              <div className="space-y-2.5">
                <Link to="/patients" className="flex items-center gap-2 px-4 py-2 border border-white/25 rounded-full text-[13px] text-white/80 hover:bg-white/10 hover:border-white/40 transition-all w-fit" data-testid="footer-patients-btn">
                  <Stethoscope size={15} className="opacity-70" /> Espace PATIENTS
                </Link>
                <Link to="/annuaire" className="flex items-center gap-2 px-4 py-2 border border-[#8ecda8]/40 rounded-full text-[13px] text-[#a8d5ba] hover:bg-[#8ecda8]/10 hover:border-[#8ecda8]/60 transition-all w-fit" data-testid="footer-annuaire-btn">
                  <Search size={15} /> Annuaire des ophtalmologistes
                </Link>
              </div>
            </div>

            {/* Col 4: Espace Praticiens — mint green with icons */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
                <span className="underline decoration-[#8ecda8] underline-offset-4">Espace Praticiens</span>
              </h3>
              <ul className="space-y-2">
                <li><Link to="/auth" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><UserCircle size={16} className="opacity-80" /> Mon compte</Link></li>
                <li><Link to="/messagerie" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><MessageSquare size={16} className="opacity-80" /> Messagerie</Link></li>
                <li><Link to="/events" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><Calendar size={16} className="opacity-80" /> Les événements</Link></li>
                <li><Link to="/nos-formations" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><BookOpen size={16} className="opacity-80" /> Les articles de formation</Link></li>
                <li><Link to="/nos-formations?cat=Les Replays" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><Play size={16} className="opacity-80" /> Les replays</Link></li>
                <li><Link to="/nos-formations?cat=Les Publications" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><Microscope size={16} className="opacity-80" /> Les publications scientifiques</Link></li>
                <li><Link to="/nos-formations?cat=Les Cours" className="flex items-center gap-2 text-[#b8dcc6] hover:text-white text-sm font-semibold transition-colors"><GraduationCap size={16} className="opacity-80" /> Les cours</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative z-10 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3.5 flex flex-col sm:flex-row justify-between items-center gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/55 text-[11px] font-semibold">
              <span><strong className="font-bold text-white">SFOALC</strong>&copy;{currentYear}</span>
              <span className="hidden sm:inline">-</span>
              <Link to="/legal" className="flex items-center gap-1 hover:text-white transition-colors"><Shield size={9} /> Mentions légales</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
              <span>|</span>
              <Link to="/conditions" className="hover:text-white transition-colors">CGU</Link>
              <span>|</span>
              <Link to="/conditions-annuaire" className="hover:text-white transition-colors" data-testid="footer-annuaire-terms-link">Conditions de l'annuaire</Link>
              {isOphtalmoConnected && (
                <>
                  <span>|</span>
                  <Link to="/roles-permissions" className="hover:text-white transition-colors" data-testid="footer-roles-link">Rôles & Permissions</Link>
                </>
              )}
            </div>
            <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 group" data-testid="msc-credit-link">
              <span className="text-white/55 text-[11px] font-semibold group-hover:text-white transition-colors">Site et application développés par</span>
              <img src="/logo-agence-msc.webp" alt="Agence MSC" className="h-4 w-auto opacity-70 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
